import React,{useEffect,useState} from 'react';
import {AbsoluteFill,Audio,Sequence,staticFile,useCurrentFrame,delayRender,continueRender,cancelRender,spring} from 'remotion';
import {makeCircle} from '@remotion/shapes';
import {Stage,Panel,Caption,SceneTransition,SlideTimeBar,FONTS,SPRING} from '../../coldcompute';
import {Illustration} from './illustrations';
import {Beat,C,FONT,LessonTiming,clamp} from './model';
import rawTiming from './timing.json';
import seriesFonts from './fonts.json';

const timing=rawTiming as LessonTiming;
export const CONTEXT_LESSON_FRAMES=timing.durationFrames;
export const CONTEXT_LESSON_TIMING=timing;

const LocalFont:React.FC=()=>{
  const [handle]=useState(()=>delayRender('context-lesson-local-font'));
  useEffect(()=>{
    const faces=[{family:'Inter',file:'inter.woff2',weight:'100 900'},...seriesFonts];
    Promise.all(faces.map(async f=>{
      const face=new FontFace(f.family,`url(${staticFile('assets/context-lesson/'+f.file)})`,{weight:f.weight});
      document.fonts.add(await face.load());
    })).then(()=>continueRender(handle)).catch(cancelRender);
  },[handle]);
  return null;
};

const TITLES:Record<string,string>={
  opening:'Why AI loses the thread',definition:'Information for the next answer',promise:'Choosing context carefully',
  'login-example':'One small follow-up','three-kinds':'Training, context and saved memory','design-choice':'Choose what the question needs',
  'follow-up':'The subject and the evidence',history:'Carry useful conversation forward',rewrite:'Give a short search its subject',
  'old-mistake':'Earlier answers can be wrong','repeat-guard':'Give current evidence a fresh chance',evidence:'Different sources prove different things',
  permission:'Check access before using a result','empty-scope':'An empty allowed set stays empty','change-during-work':'Check access during the request',
  'old-plan':'A plan can outlive its accuracy','history-size':'Give history a practical limit','evidence-budget':'Make room for useful passages',
  'time-reserve':'Leave time to explain the answer',coverage:'A useful passage can leave gaps','follow-search':'Investigate the missing part',
  reuse:'Reusing an answer also reuses assumptions','cache-status':'Check the path users actually receive','memory-status':'Old summaries need current authority',
  vision:'Continue yesterday with today in view','user-takeaway':'Three habits you can use','ending':'Choose what shapes the next answer',
};

const TOPICS=['What context means','Following the conversation','Choosing trustworthy evidence','Making room to answer','Using memory carefully'];
const LessonMap:React.FC=()=>{
  const frame=useCurrentFrame(),time=frame/30;
  const current=timing.chapters.reduce((last,c,i)=>time>=c.start?i:last,0);
  const final=frame>=timing.durationFrames-45;
  return <AbsoluteFill style={{left:1568,width:352,background:C.bg2,borderLeft:`1px solid ${C.line}`,fontFamily:FONT,padding:'61px 27px',color:C.text,zIndex:10}}>
    <div style={{fontFamily:FONTS.display,fontSize:30,fontWeight:700}}>In this lesson</div>
    <div style={{fontFamily:FONTS.mono,fontSize:18,color:C.dim,marginTop:12}}>{final?5:current} of 5 topics covered</div>
    <div style={{height:1,background:C.line,marginTop:29,marginBottom:30}}/>
    {TOPICS.map((title,i)=>{
      const active=i===current&&!final,done=i<current||final,future=i>current;
      const reveal=frame<Math.round(timing.chapters[i].start*30)?0:spring({fps:30,frame:frame-Math.round(timing.chapters[i].start*30),config:SPRING.snappy});
      return <div key={title} data-topic={i} data-state={done?'done':active?'active':'future'} style={{display:'flex',alignItems:'center',gap:17,minHeight:115,borderLeft:`3px solid ${active?C.cyan:'transparent'}`,padding:'15px 11px',marginBottom:8,background:active?'#141C26':'transparent',borderRadius:9}}>
        <div style={{width:27,height:27,flexShrink:0,border:`2px solid ${done?C.green:active?C.cyan:C.line}`,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:19,fontWeight:800,color:done?C.green:C.cyan}}>{done?'✓':active?'•':''}</div>
        <div style={{fontFamily:FONTS.display,fontSize:25,lineHeight:1.3,fontWeight:active?700:500,color:future?C.dim:C.text,opacity:future?.35:1,filter:`blur(${future?3.5:3.5*(1-clamp(reveal))}px)`}}>{title}</div>
      </div>;
    })}
    <div style={{position:'absolute',left:38,right:38,bottom:65}}>
      <div style={{fontSize:18,color:C.dim,lineHeight:1.5,marginBottom:18}}>One example.<br/>Each decision explained.</div>
      <div style={{height:4,background:C.line}}><div style={{height:4,background:C.cyan,width:`${100*clamp(frame/(timing.durationFrames-1))}%`}}/></div>
    </div>
  </AbsoluteFill>;
};

const HeroThought:React.FC<{beat:Beat;time:number}>=({beat,time})=>{
  const cueFrame=Math.max(0,(time-beat.cueTime)*30);
  const p=time<beat.cueTime?0:clamp(spring({fps:30,frame:cueFrame,config:SPRING.snappy}));
  const opening=beat.id==='opening',definition=beat.id==='definition';
  return <div style={{position:'absolute',left:70,right:70,top:235}}>
    <Panel accent={opening?'amber':definition?'cyan':'green'} padding={60} style={{minHeight:520}}>
      <div style={{fontFamily:FONTS.mono,fontSize:24,letterSpacing:5,textTransform:'uppercase',color:opening?C.amber:C.cyan,marginBottom:42}}>{opening?'One small follow-up':definition?'The idea behind the answer':'A practical design choice'}</div>
      <div style={{fontFamily:FONTS.display,fontSize:opening?99:definition?150:105,fontWeight:700,lineHeight:1.06,letterSpacing:-4,color:definition?C.cyan:C.text}}>{opening?'Is it fixed now?':definition?'CONTEXT.':<>CHOOSE<br/>CAREFULLY.</>}</div>
      <div style={{fontFamily:FONTS.display,fontSize:44,color:opening?C.amber:C.green,marginTop:34,opacity:p,transform:`translateY(${18*(1-p)}px)`}}>{opening?'It lost the subject.':definition?'Information for the next answer.':'Give the question useful information.'}</div>
    </Panel>
  </div>;
};

const Thought:React.FC<{beat:Beat;index:number}>=({beat,index})=>{
  const frame=useCurrentFrame(),time=frame/30;
  const caption=beat.captions.find(c=>time>=c.start&&time<c.end&&time<beat.audioEnd-beat.start);
  const isChapterStart=index===0||timing.scenes[index-1].chapter!==beat.chapter;
  const title=TITLES[beat.id]??beat.id;
  const hero=index<3;
  return <SceneTransition><AbsoluteFill style={{width:1568,fontFamily:FONT,color:C.text}}>
    <div style={{position:'absolute',left:60,top:43,right:55,display:'flex',alignItems:'center',justifyContent:'space-between',color:C.cyan,fontFamily:FONTS.mono,fontSize:22,letterSpacing:3}}>
      <span>EAKC / BUILDER NOTEBOOK</span><span style={{fontSize:20,color:C.dim,letterSpacing:1}}>{beat.chapter+1} / 5</span>
    </div>
    {!hero&&<><div style={{position:'absolute',left:60,top:104,right:45,fontFamily:FONTS.display,fontSize:title.length>43?50:62,fontWeight:700,lineHeight:1.12,letterSpacing:-1.6}}>{title}</div>
      <div style={{position:'absolute',left:59,top:205}}><Panel width={1450} padding={0} accent={beat.chapter===2?'green':beat.chapter===3?'amber':'cyan'}><div style={{display:'flex',justifyContent:'center'}}><Illustration beat={beat} time={time}/></div></Panel></div></>}
    {hero&&<HeroThought beat={beat} time={time}/>}
    {caption&&<Caption lines={[{scene:beat.id,fromSec:0,caption:caption.text}]} accent={beat.chapter===3?'amber':'cyan'}/>}
    {isChapterStart&&time<2.3&&!hero&&<div style={{position:'absolute',left:62,top:172,fontFamily:FONTS.mono,fontSize:18,letterSpacing:2,color:C.cyan,opacity:clamp((2.3-time)/.4)}}>{TOPICS[beat.chapter]}</div>}
  </AbsoluteFill></SceneTransition>;
};

export const ContextManagementLesson:React.FC=()=>{
  if(timing.provisional)throw new Error('Provisional narration timing cannot be exported');
  return <Stage fontLoader={<LocalFont/>}>
    <Audio src={staticFile('assets/context-lesson/narration.wav')}/>
    {timing.scenes.map((beat,index)=>{
      const from=Math.round(beat.start*30),end=Math.round(beat.end*30);
      const duration=Math.max(1,end-from);
      return <Sequence key={beat.id} from={from} durationInFrames={duration}><Thought beat={beat} index={index}/><AbsoluteFill style={{zIndex:20,pointerEvents:'none'}}><SlideTimeBar sceneDurationFrames={duration}/></AbsoluteFill></Sequence>;
    })}
    <LessonMap/>
  </Stage>;
};

export const ContextManagementLessonThumb:React.FC=()=>{
  const circle=makeCircle({radius:125});
  return <Stage grid={false} fontLoader={<LocalFont/>}>
    <div style={{position:'absolute',left:65,top:65,color:C.amber,fontFamily:FONTS.mono,fontSize:25,fontWeight:500,letterSpacing:3}}>EAKC / BUILDER NOTEBOOK</div>
    <div style={{position:'absolute',left:62,top:180,fontFamily:FONTS.display,fontSize:124,fontWeight:700,letterSpacing:-5,lineHeight:1.03}}>HOW AI<br/><span style={{color:C.cyan}}>KEEPS UP.</span></div>
    <div style={{position:'absolute',left:68,bottom:75,fontSize:35,color:C.dim}}>Context management, explained</div>
    <svg viewBox="0 0 1280 720" style={{position:'absolute',inset:0,width:'100%',height:'100%'}}>
      <path d={circle.path} transform="translate(900 215)" fill={C.panel} stroke={C.cyan} strokeWidth={4}/>
      <rect x={864} y={195} width={310} height={150} rx={30} fill={C.panel2} stroke={C.cyan} strokeWidth={3}/>
      <text x={1019} y={255} textAnchor="middle" fill={C.text} fontFamily={FONT} fontSize={32}>Is it fixed now?</text>
      <path d="M920 345C920 420 890 450 840 450" fill="none" stroke={C.cyan} strokeWidth={5}/>
      <rect x={735} y={405} width={285} height={150} rx={18} fill={C.panel2} stroke={C.green} strokeWidth={3}/>
      <text x={877} y={460} textAnchor="middle" fill={C.green} fontFamily={FONT} fontSize={29}>The login bug</text>
      <path d="M824 502l20 19 40-43" fill="none" stroke={C.green} strokeWidth={7} strokeLinecap="round"/>
    </svg>
  </Stage>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
